import { Controller } from "@hotwired/stimulus"

// Animates the permanent brand label like a small terminal prompt during Turbo navigation.
export default class extends Controller {
  static values = {
    defaultText: { type: String, default: "WeAreDevelopers" },
    globalSearchHint: { type: String, default: "Global Search" },
    deleteDelay: { type: Number, default: 70 },
    pauseDelay: { type: Number, default: 260 },
    typeDelay: { type: Number, default: 95 }
  }

  connect() {
    this.animationToken = 0
    this.lazySearchResults = []
    this.searchLoading = false
    this.selectedIndex = 0
    this.targetText = this.element.textContent.trim() || this.defaultTextValue

    this.handleBeforeCache = this.handleBeforeCache.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)
    this.handleDocumentPaste = this.handleDocumentPaste.bind(this)
    this.handleLoad = this.handleLoad.bind(this)

    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    document.addEventListener("turbo:load", this.handleLoad)
    document.addEventListener("click", this.handleDocumentClick)
    document.addEventListener("keydown", this.handleDocumentKeydown, { capture: true })
    document.addEventListener("paste", this.handleDocumentPaste)
    this.triggerElement?.addEventListener("click", this.handleClick)

    this.themeObserver = new MutationObserver(() => this.handleLoad())
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })

    this.handleLoad()
  }

  disconnect() {
    this.closeConsole()
    this.clearTimer()
    this.clearSearchRequest()
    this.themeObserver?.disconnect()
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    document.removeEventListener("turbo:load", this.handleLoad)
    document.removeEventListener("click", this.handleDocumentClick)
    document.removeEventListener("keydown", this.handleDocumentKeydown, { capture: true })
    document.removeEventListener("paste", this.handleDocumentPaste)
    this.triggerElement?.removeEventListener("click", this.handleClick)
  }

  handleBeforeCache() {
    this.closeConsole({ restore: !this.keepVisibleTextForNavigation })
    this.clearTimer()

    if (!this.keepVisibleTextForNavigation) {
      this.setText(this.targetText || this.titleFromBody(document.body) || this.defaultTextValue)
    }
  }

  handleLoad() {
    const nextText = this.titleFromBody(document.body)

    this.keepVisibleTextForNavigation = false

    if (this.consoleMode) return

    if (this.terminalThemeActive) {
      this.animateTo(nextText)
    } else {
      this.closeConsole()
      this.swapTo(this.defaultTextValue)
    }
  }

  handleClick(event) {
    if (!this.terminalThemeActive) return

    event.preventDefault()
    event.stopPropagation()
    this.openKeyboardConsole("")
  }

  handleDocumentClick(event) {
    if (!this.consoleMode) return
    if (this.triggerElement?.contains(event.target)) return
    if (this.resultsElement?.contains(event.target)) return

    this.closeConsole()
  }

  handleDocumentKeydown(event) {
    if (!this.terminalThemeActive) return
    if (event.defaultPrevented) return

    if (this.consoleMode === "keyboard") {
      this.handleKeyboardConsoleKeydown(event)
      event.stopPropagation()
      return
    }

    if (this.interactiveEventTarget(event.target)) return
    if (event.metaKey || event.altKey) return

    if (event.ctrlKey) {
      this.handleInactiveControlKeydown(event)
      return
    }

    if (event.key === "Escape" || event.code === "Backquote" || (event.shiftKey && event.code === "Digit7")) {
      event.preventDefault()
      this.openKeyboardConsole("")
      return
    }

    if (event.key === "Backspace") {
      event.preventDefault()
      this.openKeyboardConsole(this.currentTitleText.slice(0, -1))
      return
    }

    if (event.key.length !== 1) return

    event.preventDefault()
    this.openKeyboardConsole(`${this.currentTitleText}${event.key}`)
  }

  handleDocumentPaste(event) {
    if (!this.terminalThemeActive) return
    if (event.defaultPrevented) return
    if (this.interactiveEventTarget(event.target)) return

    const pastedText = event.clipboardData?.getData("text").replace(/\s+/g, " ").trim()
    if (!pastedText) return

    event.preventDefault()

    if (this.consoleMode === "keyboard") {
      this.updateConsoleQuery(this.consoleQuery({ preserveCase: true, trim: false }) + pastedText)
    } else {
      this.openKeyboardConsole(pastedText)
    }
  }

  handleKeyboardConsoleKeydown(event) {
    if (event.metaKey || event.altKey) return

    if (event.ctrlKey) {
      this.handleControlKeydown(event)
      return
    }

    if (this.handleResultNavigationKeydown(event)) return

    switch (event.key) {
      case "Backspace":
        event.preventDefault()
        this.updateConsoleQuery(this.consoleQuery({ preserveCase: true, trim: false }).slice(0, -1), { sound: { delete: true } })
        break
      case "Delete":
        event.preventDefault()
        this.updateConsoleQuery("", { sound: { delete: true } })
        break
      default:
        if (event.key.length !== 1) return

        event.preventDefault()
        this.updateConsoleQuery(this.consoleQuery({ preserveCase: true, trim: false }) + event.key)
    }
  }

  handleResultNavigationKeydown(event) {
    switch (event.key) {
      case "Escape":
        event.preventDefault()
        this.closeConsole()
        return true
      case "/":
      case "&":
        if (!event.shiftKey || event.code !== "Digit7") return false

        event.preventDefault()
        this.closeConsole()
        return true
      case "ArrowDown":
        if (!this.resultsElement) return false

        event.preventDefault()
        this.playKeySound({ navigate: true })
        this.selectResult(this.selectedIndex + 1)
        return true
      case "ArrowUp":
        if (!this.resultsElement) return false

        event.preventDefault()
        this.playKeySound({ navigate: true })
        this.selectResult(this.selectedIndex - 1)
        return true
      case "Enter":
        if (!this.resultsElement) return false

        event.preventDefault()
        this.playKeySound({ enter: true })
        this.acceptSelectedResult()
        return true
      default:
        return false
    }
  }

  handleControlKeydown(event) {
    switch (event.key.toLowerCase()) {
      case "c":
        event.preventDefault()
        this.closeConsole()
        break
      case "u":
        event.preventDefault()
        this.updateConsoleQuery("")
        break
      case "h":
        event.preventDefault()
        this.updateConsoleQuery(this.consoleQuery({ preserveCase: true, trim: false }).slice(0, -1), { sound: { delete: true } })
        break
      case "w":
        event.preventDefault()
        this.updateConsoleQuery(this.clearPreviousWord(this.consoleQuery({ preserveCase: true, trim: false })))
        break
    }
  }

  handleInactiveControlKeydown(event) {
    switch (event.key.toLowerCase()) {
      case "u":
        event.preventDefault()
        this.openKeyboardConsole("")
        break
      case "h":
        event.preventDefault()
        this.openKeyboardConsole(this.currentTitleText.slice(0, -1), { sound: { delete: true } })
        break
      case "w":
        event.preventDefault()
        this.openKeyboardConsole(this.clearPreviousWord(this.currentTitleText))
        break
    }
  }

  clearPreviousWord(value) {
    return value.replace(/\s*\S+\s*$/, "")
  }

  openKeyboardConsole(initialQuery = null, { sound = null } = {}) {
    if (!this.terminalThemeActive) return

    this.clearTimer()
    this.clearSearchRequest()
    this.consoleMode = "keyboard"
    this.selectedIndex = 0
    this.previousText = this.element.textContent.trim() || this.targetText || this.titleFromBody(document.body) || this.defaultTextValue
    this.consoleQueryText = initialQuery ?? this.previousText
    this.consoleAnchorLeft = this.element.getBoundingClientRect().left

    this.element.classList.add("terminal-title--editing")
    this.element.setAttribute("role", "combobox")
    this.element.setAttribute("aria-expanded", "false")
    this.element.setAttribute("aria-label", "Terminal navigation command")

    this.setText(this.consoleQueryText, { sound })
    this.searchLoading = this.consoleQuery().length > 0
    this.renderConsoleResults()
    this.scheduleSearchResults()
  }

  closeConsole({ restore = true } = {}) {
    if (!this.consoleMode && !this.resultsElement) return

    this.consoleMode = null
    this.consoleQueryText = null
    this.consoleAnchorLeft = null
    this.lazySearchResults = []
    this.clearSearchRequest()
    this.element.classList.remove("terminal-title--editing")
    this.hideResults()
    this.element.removeAttribute("role")
    this.element.removeAttribute("aria-expanded")
    this.element.removeAttribute("aria-label")
    if (restore) {
      this.setText(this.targetText || this.previousText || this.titleFromBody(document.body) || this.defaultTextValue)
    }
  }

  renderConsoleResults({ ignoreQuery = false } = {}) {
    this.hideResults()

    const results = this.filteredCommands({ ignoreQuery })
    this.results = results
    this.resultsElement = document.createElement("div")
    this.resultsElement.className = "terminal-title-console"
    this.resultsElement.setAttribute("role", "listbox")
    this.setConsoleExpanded(true)

    const rect = this.element.getBoundingClientRect()
    const compactViewport = window.matchMedia("(max-width: 640px)").matches
    const anchorLeft = this.consoleAnchorLeft ?? rect.left
    const left = compactViewport ? 0 : Math.max(16, anchorLeft)
    const rightMargin = compactViewport ? 0 : 16

    this.resultsElement.style.left = `${left}px`
    this.resultsElement.style.right = `${rightMargin}px`
    this.resultsElement.style.top = `${rect.bottom + 10}px`

    results.forEach((result, index) => {
      const button = document.createElement("button")
      const label = document.createElement("span")
      const hint = document.createElement("small")

      button.type = "button"
      button.className = "terminal-title-console__item"
      button.dataset.active = index === this.selectedIndex ? "true" : "false"
      label.textContent = result.label
      hint.textContent = result.hint
      button.append(label, hint)
      button.addEventListener("mousedown", (event) => event.preventDefault())
      button.addEventListener("click", () => this.acceptResult(result))
      this.resultsElement.appendChild(button)
    })

    if (this.searchLoading) {
      const loading = document.createElement("div")
      loading.className = "terminal-title-console__loading"
      loading.textContent = "searching…"
      this.resultsElement.appendChild(loading)
    }

    this.resultsElement.appendChild(this.shortcutHintElement())

    document.body.appendChild(this.resultsElement)
  }

  shortcutHintElement() {
    const hint = document.createElement("div")
    const shortcuts = [
      ["↑↓", "select"],
      ["enter", "submit"],
      ["ctrl-w", "clear word"],
      ["ctrl-u", "clear"],
      ["esc", "close"]
    ]

    hint.className = "terminal-title-console__hint"

    shortcuts.forEach(([keys, label]) => {
      const kbd = document.createElement("kbd")
      const text = document.createTextNode(` ${label} `)

      kbd.textContent = keys
      hint.append(kbd, text)
    })

    return hint
  }

  hideResults() {
    this.resultsElement?.remove()
    this.resultsElement = null
    this.results = null
    this.setConsoleExpanded(false)
  }

  setConsoleExpanded(expanded) {
    const value = expanded ? "true" : "false"
    this.element.setAttribute("aria-expanded", value)
  }

  filteredCommands({ ignoreQuery = false } = {}) {
    const query = ignoreQuery ? "" : this.consoleQuery()
    const commands = this.commands()
    const matchingCommands = query ? commands.filter((command) => command.label.toLowerCase().includes(query)) : commands
    const commandLimit = this.lazySearchResults.length ? 4 : 8
    const results = [ ...matchingCommands.slice(0, commandLimit), ...this.lazySearchResults ]

    if (query) {
      results.push({ label: `Search “${this.consoleQuery({ preserveCase: true })}”`, hint: this.globalSearchHintValue, url: `/search?q=${encodeURIComponent(this.consoleQuery({ preserveCase: true }))}` })
    }

    return results.slice(0, 12)
  }

  commands() {
    const links = Array.from(document.querySelectorAll(".navbar-center a, .mobile-nav-menu a"))
    const commands = [{ label: "Home", hint: "/", url: "/" }]

    links.forEach((link) => {
      const label = link.textContent.trim().replace(/\s+/g, " ")
      if (!label) return

      commands.push({ label, hint: new URL(link.href).pathname, url: link.href })
    })

    return Array.from(new Map(commands.map((command) => [`${command.label}-${command.url}`, command])).values())
  }

  updateConsoleQuery(query, { sound = null } = {}) {
    this.consoleQueryText = query
    this.selectedIndex = 0
    this.lazySearchResults = []
    this.setText(query, { sound })

    this.searchLoading = this.consoleQuery().length > 0
    this.renderConsoleResults()
    this.scheduleSearchResults()
  }

  scheduleSearchResults() {
    const query = this.consoleQuery({ preserveCase: true })

    this.clearSearchRequest()
    this.searchLoading = query.length > 0

    if (!query) {
      this.lazySearchResults = []
      this.renderConsoleResults()
      return
    }

    this.searchTimer = window.setTimeout(() => this.fetchSearchResults(query), 180)
  }

  fetchSearchResults(query) {
    this.searchAbortController = new AbortController()

    fetch(`/search/fetch.json?q=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" },
      signal: this.searchAbortController.signal
    })
      .then((response) => response.ok ? response.json() : [])
      .then((results) => {
        if (!this.consoleMode || this.consoleQuery({ preserveCase: true }) !== query) return

        this.lazySearchResults = results
        this.searchLoading = false
        this.renderConsoleResults()
      })
      .catch((error) => {
        if (error.name === "AbortError" || !this.consoleMode) return

        this.lazySearchResults = []
        this.searchLoading = false
        this.renderConsoleResults()
      })
  }

  clearSearchRequest() {
    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer)
      this.searchTimer = null
    }

    this.searchAbortController?.abort()
    this.searchAbortController = null
    this.searchLoading = false
  }

  selectResult(index) {
    if (!this.results?.length) return

    this.selectedIndex = (index + this.results.length) % this.results.length
    Array.from(this.resultsElement.children).forEach((child, childIndex) => {
      child.dataset.active = childIndex === this.selectedIndex ? "true" : "false"
    })
  }

  acceptSelectedResult() {
    const result = this.results?.[this.selectedIndex]
    if (result) this.acceptResult(result)
  }

  acceptResult(result) {
    this.keepVisibleTextForNavigation = true
    this.closeConsole({ restore: false })

    if (new URL(result.url, window.location.href).href === window.location.href) {
      this.animateTo(this.titleFromBody(document.body) || this.defaultTextValue)
      return
    }

    if (window.Turbo) {
      window.Turbo.visit(result.url)
    } else {
      window.location.href = result.url
    }
  }

  consoleQuery({ preserveCase = false, trim = true } = {}) {
    const rawQuery = this.consoleQueryText || ""
    const query = trim ? rawQuery.trim() : rawQuery

    return preserveCase ? query : query.toLowerCase()
  }

  animateTo(nextText) {
    const normalizedNextText = nextText?.trim()
    if (!normalizedNextText) return

    const currentText = this.element.textContent
    if (normalizedNextText === this.targetText && normalizedNextText === currentText) return

    this.targetText = normalizedNextText

    if (normalizedNextText === currentText || this.prefersReducedMotion) {
      this.setText(normalizedNextText)
      return
    }

    const token = ++this.animationToken
    this.clearTimer()
    this.deleteCharacters(currentText, normalizedNextText, token)
  }

  swapTo(nextText) {
    const normalizedNextText = nextText?.trim()
    if (!normalizedNextText) return

    this.clearTimer()
    this.targetText = normalizedNextText
    this.setText(normalizedNextText)
  }

  deleteCharacters(currentText, nextText, token) {
    if (!this.animationActive(token)) return

    const characters = Array.from(currentText)
    if (characters.length === 0) {
      this.timer = window.setTimeout(() => this.typeCharacters("", nextText, token), this.pauseDelayValue)
      return
    }

    const updatedText = characters.slice(0, -1).join("")
    this.setText(updatedText)
    this.timer = window.setTimeout(() => this.deleteCharacters(updatedText, nextText, token), this.deleteDelayValue)
  }

  typeCharacters(currentText, nextText, token) {
    if (!this.animationActive(token)) return

    const nextCharacters = Array.from(nextText)
    const currentLength = Array.from(currentText).length
    if (currentLength >= nextCharacters.length) {
      this.setText(nextText)
      return
    }

    const updatedText = nextCharacters.slice(0, currentLength + 1).join("")
    this.setText(updatedText)
    this.timer = window.setTimeout(() => this.typeCharacters(updatedText, nextText, token), this.typeDelayValue)
  }

  interactiveEventTarget(target) {
    return target?.closest?.("a, button, input, textarea, select, summary, media-player, media-provider, [contenteditable], [role='button'], [role='link'], [role='checkbox'], [role='radio'], [role='switch'], [role='tab'], [role='menuitem'], [role='option'], [role='textbox']")
  }

  playKeySound({ enter = false, delete: deleteKey = false, navigate = false } = {}) {
    const AudioContext = window.AudioContext || window.webkitAudioContext
    if (!AudioContext) return

    this.audioContext ||= new AudioContext()

    const now = this.audioContext.currentTime
    const oscillator = this.audioContext.createOscillator()
    const gain = this.audioContext.createGain()
    const shortTone = deleteKey || navigate

    oscillator.type = shortTone ? "triangle" : "square"
    oscillator.frequency.setValueAtTime(enter ? 660 : navigate ? 440 + Math.random() * 30 : deleteKey ? 360 + Math.random() * 40 : 1040 + Math.random() * 120, now)
    gain.gain.setValueAtTime(shortTone ? 0.014 : 0.018, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + (shortTone ? 0.026 : 0.018))

    oscillator.connect(gain)
    gain.connect(this.audioContext.destination)
    oscillator.start(now)
    oscillator.stop(now + (shortTone ? 0.028 : 0.02))
  }

  animationActive(token) {
    return token === this.animationToken
  }

  clearTimer() {
    if (!this.timer) return

    window.clearTimeout(this.timer)
    this.timer = null
  }

  setText(text, { sound = null } = {}) {
    const nextText = text.toString()
    const changed = this.element.textContent !== nextText

    this.element.textContent = nextText

    if (changed && this.terminalThemeActive && this.consoleMode !== "input") {
      this.playKeySound(sound || {})
    }
  }

  titleFromBody(body) {
    return body?.dataset?.terminalNavigationTitle
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  get terminalThemeActive() {
    return document.documentElement.dataset.theme === "terminal"
  }

  get currentTitleText() {
    return this.element.textContent.trim() || this.targetText || this.titleFromBody(document.body) || this.defaultTextValue
  }

  get triggerElement() {
    return this.element.closest(".terminal-title-trigger") || this.element
  }
}
